import { Box, Paper, Stack } from '@mui/material';
import { Sparkle } from '@sweep-io/sweep-design/dist/icons/Sparkle';
import { Plus } from '@sweep-io/sweep-design/dist/icons/Plus';
import { Cube } from '@sweep-io/sweep-design/dist/icons/Cube';
import { Refresh } from '@sweep-io/sweep-design/dist/icons/Refresh';
import { Settings } from '@sweep-io/sweep-design/dist/icons/Settings';
import { IconButton, Tooltip } from '@sweep-io/sweep-design';
import { useAddInitialElementsToMap } from '../hooks/useAddInitialElementsToMap';
import { useAddTreatmentReviewStage } from '../hooks/useAddTreatmentReviewStage';
import { useAddFlowMetadataElement } from '../hooks/useAddFlowMetadataElement';
import { useResetHealthcareFunnels } from '../hooks/useResetHealthcareFunnels';
import { useDemoSettingsStore } from '../hooks/useDemoSettingsStore';
import { useDemoSettingsModal } from '../hooks/useDemoSettingsModal';
import { DemoSettingsPanel } from './DemoSettingsPanel';
import { useState } from 'react';

export const DemoQuickActionsPanel = () => {
  const { addInitialElementsToMap } = useAddInitialElementsToMap();
  const { addTreatmentReviewStage } = useAddTreatmentReviewStage();
  const { addTreatmentFlowMetadata } = useAddFlowMetadataElement();
  const { resetHealthcareFunnels } = useResetHealthcareFunnels();
  const { commandMapping, updateCommand, delayConfiguration, updateDelay } = useDemoSettingsStore();
  const { isOpen: isSettingsOpen, openModal: openSettings, closeModal: closeSettings } = useDemoSettingsModal();
  const [isAddingFunnels, setIsAddingFunnels] = useState(false);

  const handleAddFunnels = async () => {
    if (isAddingFunnels) {
      return;
    }

    try {
      setIsAddingFunnels(true);
      await addInitialElementsToMap();
    } catch (error) {
      console.error('Failed to add healthcare funnels:', error);
      // TODO: Add proper error handling/notification
    } finally {
      setIsAddingFunnels(false);
    }
  };


  const handleAddStage = () => {
    console.log('➕ Add Treatment Review stage button clicked');
    addTreatmentReviewStage();
  };

  const handleAddMetadata = () => {
    console.log('🔧 Add Treatment Flow metadata element & connect to Treatment Review button clicked');
    addTreatmentFlowMetadata();
  };

  const handleReset = async () => {
    console.log('🔄 Reset healthcare funnels button clicked');
    try {
      await resetHealthcareFunnels();
    } catch (error) {
      console.error('Failed to reset healthcare funnels:', error);
    }
  };

  const isFunnelsButtonDisabled = isAddingFunnels;

  //In order to make the panel visible - remove this
  return <DemoSettingsPanel
    commandMapping={commandMapping}
    onUpdateCommand={updateCommand}
    delayConfiguration={delayConfiguration}
    onUpdateDelay={updateDelay}
    open={isSettingsOpen}
    onClose={closeSettings}
  />;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          padding: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          backgroundColor: 'white',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Tooltip title={isAddingFunnels ? 'Adding healthcare funnels...' : 'Quick Add: Healthcare Funnels'}>
            <span>
              <IconButton
                variant="flat"
                onClick={handleAddFunnels}
                disabled={isFunnelsButtonDisabled}
                data-testid="quick-add-funnels-button"
                size="small"
              >
                <Sparkle />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Add 'Treatment Review' Stage (In Treatment → Treatment Review → Completed)">
            <span>
              <IconButton
                variant="flat"
                onClick={handleAddStage}
                data-testid="add-stage-button"
                size="small"
              >
                <Plus />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Add 'Treatment Flow Automation' Metadata Element & Connect to Treatment Review">
            <span>
              <IconButton
                variant="flat"
                onClick={handleAddMetadata}
                data-testid="add-metadata-button"
                size="small"
              >
                <Cube />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Reset Demo - Delete Healthcare Funnels & Metadata">
            <span>
              <IconButton
                variant="flat"
                onClick={handleReset}
                data-testid="reset-healthcare-funnels-button"
                size="small"
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>

          {/* Divider */}
          <Box sx={{ width: '1px', height: 24, bgcolor: 'grey.300', mx: 0.5 }} />

          <Tooltip title="Demo Settings (F2)">
            <span>
              <IconButton
                variant="flat"
                onClick={openSettings}
                data-testid="open-demo-settings-button"
                size="small"
              >
                <Settings />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <DemoSettingsPanel
        commandMapping={commandMapping}
        onUpdateCommand={updateCommand}
        delayConfiguration={delayConfiguration}
        onUpdateDelay={updateDelay}
        open={isSettingsOpen}
        onClose={closeSettings}
      />
    </Box>
  );
};
